import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  numericToNumber,
  type PlanCandidate,
  type PlanImportResponse,
  type PlanRiskFlag,
  type PlanSummary,
} from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";
import { cn } from "@/lib/utils";

// PlanDetail (v2.12.0) — one investment plan's workspace.
// Top: metadata + import panel + export + promote buttons.
// Main: candidate table (only THIS plan's candidates, never the channel
// library at large — that's the whole point of the workbench).
//
// Day 5 base: read-only display + import + export + promote.
// Day 6 will add inline price/tier/inclusion editing + funnel projection
// panel + per-candidate detail drawer.

const STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  promoted: "已转投放",
  archived: "已归档",
};

const STATUS_VARIANT: Record<
  string,
  "success" | "info" | "secondary" | "danger" | "warning"
> = {
  draft: "info",
  promoted: "success",
  archived: "secondary",
};

const TIER_LABEL: Record<string, string> = {
  perception: "感知",
  core: "核心",
  secondary: "二线",
};

const ENRICHMENT_LABEL: Record<string, string> = {
  pending: "拉取中",
  enriched: "已就绪",
  failed: "失败",
};

const ENRICHMENT_VARIANT: Record<
  string,
  "success" | "info" | "secondary" | "danger" | "warning"
> = {
  pending: "info",
  enriched: "success",
  failed: "danger",
};

export default function PlanDetail() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = Number(params.id);
  const [showImport, setShowImport] = useState(false);
  const [showPromote, setShowPromote] = useState(false);

  const planQ = useQuery({
    queryKey: ["plan", id],
    queryFn: () => endpoints.plan(id),
    enabled: Number.isFinite(id),
    // Poll while there are pending enrichments so the candidate table
    // animates from "拉取中" → "已就绪" without a manual refresh.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      return data.plan.pending_enrichment_count > 0 ? 5_000 : false;
    },
  });

  const archiveMut = useMutation({
    mutationFn: () => endpoints.archivePlan(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plans"] });
      qc.invalidateQueries({ queryKey: ["plan", id] });
      navigate("/plans", { replace: true });
    },
  });

  if (!Number.isFinite(id)) {
    return <div className="p-6 text-sm text-destructive">无效的方案 ID</div>;
  }
  if (planQ.isLoading) {
    return <p className="p-4 text-sm text-muted-foreground">加载中…</p>;
  }
  if (planQ.isError || !planQ.data) {
    return (
      <p className="p-4 text-sm text-destructive">
        加载失败:{(planQ.error as Error)?.message ?? "unknown"}
      </p>
    );
  }

  const { plan, candidates } = planQ.data;
  const canEdit = plan.status === "draft";
  const totalSpend = candidates
    .filter((c) => c.IncludedInFinal)
    .map((c) => numericToNumber(c.QuotedPriceUsd) ?? 0)
    .reduce((a, b) => a + b, 0);

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <Link
            to="/plans"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← 投放筛选
          </Link>
          <h1 className="text-lg font-semibold">{plan.name}</h1>
          <Badge variant={STATUS_VARIANT[plan.status] ?? "secondary"}>
            {STATUS_LABEL[plan.status] ?? plan.status}
          </Badge>
          <span className="text-xs uppercase text-muted-foreground">
            {plan.platform}
          </span>
          <div className="ml-auto flex gap-2">
            {canEdit && (
              <>
                <Button
                  size="sm"
                  onClick={() => setShowImport(true)}
                  variant="outline"
                >
                  + 导入候选
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(endpoints.planExportURL(id), "_blank")}
                  disabled={candidates.length === 0}
                  title="下载 Markdown — 粘贴到 Claude Chat 做最终筛选"
                >
                  ↓ 导出 .md
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowPromote(true)}
                  disabled={plan.selected_count === 0}
                  title={
                    plan.selected_count === 0
                      ? "至少需要 1 个入选候选"
                      : "把入选频道一次性建成新投放"
                  }
                >
                  → 转投放 ({plan.selected_count})
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (confirm("归档后不可再编辑,确认?")) {
                      archiveMut.mutate();
                    }
                  }}
                >
                  归档
                </Button>
              </>
            )}
            {plan.status === "promoted" && plan.campaign_id && (
              <Link
                to={`/campaigns/${plan.campaign_id}`}
                className="inline-flex items-center rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-muted"
              >
                → 查看投放 #{plan.campaign_id}
              </Link>
            )}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span>候选: {plan.candidate_count}</span>
          <span>入选: {plan.selected_count}</span>
          {plan.pending_enrichment_count > 0 && (
            <span className="text-amber-600">
              拉取中: {plan.pending_enrichment_count}
            </span>
          )}
          {plan.budget_usd != null && (
            <span>
              预算: ${fmtUSD(plan.budget_usd)} · 已分配: ${fmtUSD(totalSpend)}
            </span>
          )}
          {plan.notes && <span>· {plan.notes}</span>}
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-background">
        {candidates.length === 0 ? (
          <EmptyState onImport={() => setShowImport(true)} canEdit={canEdit} />
        ) : (
          <CandidateTable candidates={candidates} />
        )}
      </div>

      {showImport && (
        <ImportModal
          planID={id}
          onClose={() => setShowImport(false)}
          onDone={() => {
            setShowImport(false);
            qc.invalidateQueries({ queryKey: ["plan", id] });
            qc.invalidateQueries({ queryKey: ["plans"] });
          }}
        />
      )}
      {showPromote && (
        <PromoteModal
          plan={plan}
          onClose={() => setShowPromote(false)}
          onDone={(campaignID) => {
            setShowPromote(false);
            qc.invalidateQueries({ queryKey: ["plans"] });
            qc.invalidateQueries({ queryKey: ["plan", id] });
            navigate(`/campaigns/${campaignID}`);
          }}
        />
      )}
    </div>
  );
}

function EmptyState({
  onImport,
  canEdit,
}: {
  onImport: () => void;
  canEdit: boolean;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-sm text-muted-foreground">
      <p>这个方案还没有候选。</p>
      {canEdit && (
        <Button size="sm" onClick={onImport}>
          + 导入候选 (粘贴 agency 给的 list)
        </Button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Candidate table — read-only in Day 5. Day 6 adds inline price/tier/include.
// -----------------------------------------------------------------------------

function CandidateTable({ candidates }: { candidates: PlanCandidate[] }) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>#</TH>
          <TH>Handle</TH>
          <TH className="text-right">订阅</TH>
          <TH className="text-right">中位播</TH>
          <TH className="text-right">ER%</TH>
          <TH className="text-right">V/S%</TH>
          <TH className="text-right">报价</TH>
          <TH className="text-right">CPM</TH>
          <TH>档位</TH>
          <TH>主题</TH>
          <TH>风险</TH>
          <TH>入选</TH>
          <TH>状态</TH>
        </TR>
      </THead>
      <TBody>
        {candidates.map((c, idx) => (
          <CandidateRow key={c.CandidateID} c={c} idx={idx + 1} />
        ))}
      </TBody>
    </Table>
  );
}

function CandidateRow({ c, idx }: { c: PlanCandidate; idx: number }) {
  const er = numericToNumber(c.EngagementRate);
  const quote = numericToNumber(c.QuotedPriceUsd);
  const promo = numericToNumber(c.PromoRatio);
  const cpm =
    quote != null && c.MedianViews90d > 0
      ? (quote * 1000) / c.MedianViews90d
      : null;
  const vs =
    c.MedianViews90d > 0 && c.FollowerCount && c.FollowerCount > 0
      ? c.MedianViews90d / c.FollowerCount
      : null;
  const handleLabel = c.Handle ?? c.Name ?? c.PlatformChannelID;
  return (
    <TR className={!c.IncludedInFinal ? "opacity-50" : undefined}>
      <TD className="text-xs text-muted-foreground tabular-nums">{idx}</TD>
      <TD>
        <Link
          to={`/channels/${c.ID}`}
          className="font-medium hover:underline"
          title={c.Name ?? undefined}
        >
          {handleLabel}
        </Link>
        {c.PriorPlanCount > 0 && (
          <div className="text-xs text-amber-600">
            ⚠ 曾在 {c.PriorPlanCount} 个其他 Plan
            {c.LastPromotedAt && (
              <span> · 上次 promote {c.LastPromotedAt.slice(0, 10)}</span>
            )}
          </div>
        )}
      </TD>
      <TD className="text-right tabular-nums">
        {fmtBigNumber(c.FollowerCount)}
      </TD>
      <TD className="text-right tabular-nums">
        {fmtBigNumber(c.MedianViews90d)}
      </TD>
      <TD className="text-right tabular-nums">
        {er != null ? `${(er * 100).toFixed(2)}%` : "—"}
      </TD>
      <TD className="text-right tabular-nums">
        {vs != null ? `${(vs * 100).toFixed(1)}%` : "—"}
      </TD>
      <TD className="text-right tabular-nums">
        {quote != null ? `$${fmtUSD(quote)}` : "—"}
      </TD>
      <TD className="text-right tabular-nums">
        {cpm != null ? `$${cpm.toFixed(2)}` : "—"}
      </TD>
      <TD>
        {c.Tier ? (
          <span className={cn("text-xs", c.TierOverride && "italic")}>
            {TIER_LABEL[c.Tier] ?? c.Tier}
            {c.TierOverride && (
              <span className="text-muted-foreground"> (手)</span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TD>
      <TD>
        {c.Themes && c.Themes.length > 0 ? (
          <span className="text-xs text-muted-foreground">
            {c.Themes.slice(0, 2)
              .map((t) => t.key)
              .join(", ")}
          </span>
        ) : promo != null ? (
          <span className="text-xs text-muted-foreground">
            推广 {promo.toFixed(0)}%
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TD>
      <TD>
        <RiskBadges flags={c.RiskFlags} />
      </TD>
      <TD>
        {c.IncludedInFinal ? (
          <span className="text-emerald-600">✓</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TD>
      <TD>
        <Badge variant={ENRICHMENT_VARIANT[c.EnrichmentState] ?? "secondary"}>
          {ENRICHMENT_LABEL[c.EnrichmentState] ?? c.EnrichmentState}
        </Badge>
      </TD>
    </TR>
  );
}

function RiskBadges({ flags }: { flags: PlanRiskFlag[] | null }) {
  if (!flags || flags.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const red = flags.filter((f) => f.level === "red").length;
  const yellow = flags.filter((f) => f.level === "yellow").length;
  const info = flags.filter((f) => f.level === "info").length;
  const titleLines = flags.map((f) => `${f.level}: ${f.detail}`).join("\n");
  return (
    <span className="space-x-1 text-xs" title={titleLines}>
      {red > 0 && <span className="text-rose-600">🔴×{red}</span>}
      {yellow > 0 && <span className="text-amber-600">🟡×{yellow}</span>}
      {info > 0 && <span className="text-sky-600">ℹ️×{info}</span>}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Import modal — paste handle/URL list, one per line.
// -----------------------------------------------------------------------------

function ImportModal({
  planID,
  onClose,
  onDone,
}: {
  planID: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<PlanImportResponse | null>(null);

  const importMut = useMutation({
    mutationFn: () => endpoints.importPlanCandidates(planID, text),
    onSuccess: (r) => setResult(r),
  });

  const lineCount = text
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && !s.startsWith("#")).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-background shadow-lg">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold">导入候选频道</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            一行一个 handle 或 URL。# 开头的行作为注释忽略。已在频道库的会复用,
            新频道自动创建并并发拉取数据(YT API 约 1s/个 × 8 并发,134 行需 ~17s)。
          </p>
        </div>
        <div className="px-5 py-4">
          {result == null ? (
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`@cryptodarkness
@PrimeInvestors
https://www.youtube.com/@WEB3.Revolution
# 下面这两行会忽略
# @internal_note`}
              className="h-64 w-full rounded-md border border-input bg-background p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring"
            />
          ) : (
            <ImportResultPanel r={result} />
          )}
          {importMut.isError && (
            <p className="mt-2 text-xs text-destructive">
              {(importMut.error as Error).message}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-muted-foreground">
          <span>
            {result == null
              ? `${lineCount} 条待导入`
              : `${result.lines.length} 条已处理`}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              {result == null ? "取消" : "关闭"}
            </Button>
            {result == null ? (
              <Button
                size="sm"
                disabled={lineCount === 0 || importMut.isPending}
                onClick={() => importMut.mutate()}
              >
                {importMut.isPending
                  ? "导入并拉取中…"
                  : `导入并拉取 ${lineCount} 条`}
              </Button>
            ) : (
              <Button size="sm" onClick={onDone}>
                完成
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImportResultPanel({ r }: { r: PlanImportResponse }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-4 text-sm">
        <Stat label="已在库" value={r.existing} color="text-emerald-600" />
        <Stat label="新建" value={r.new} color="text-sky-600" />
        <Stat label="重复" value={r.duplicates} color="text-muted-foreground" />
        {r.skipped_rejected > 0 && (
          <Stat
            label="已 reject 跳过"
            value={r.skipped_rejected}
            color="text-amber-600"
          />
        )}
        <Stat label="失败" value={r.errors} color="text-rose-600" />
      </div>
      {(r.new > 0 || r.fetch_failed > 0) && (
        <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
          <span className="text-muted-foreground">新频道数据拉取:</span>{" "}
          <span className="text-emerald-600">{r.fetched} 成功</span>
          {r.fetch_failed > 0 && (
            <>
              {" · "}
              <span className="text-amber-600">{r.fetch_failed} 失败</span>
              <span className="ml-2 text-muted-foreground">
                (失败的会由定时任务在凌晨再次尝试,也可单条手动刷新)
              </span>
            </>
          )}
        </div>
      )}
      <div className="max-h-64 overflow-auto rounded-md border border-border p-2 font-mono text-xs">
        {r.lines.map((l, i) => (
          <div
            key={i}
            className={cn(
              "py-0.5",
              l.status === "error" && "text-rose-600",
              l.status === "duplicate" && "text-muted-foreground",
              l.status === "skipped_rejected" && "text-amber-600",
              l.status === "new" && "text-sky-600",
              l.status === "existing" && "text-emerald-600",
            )}
          >
            [{l.status}
            {l.fetch_failed && <span className="text-amber-600"> · 未拉取</span>}
            ] {l.input}
            {l.reason && (
              <span className="text-muted-foreground"> — {l.reason}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div>
      <div className={cn("text-2xl font-semibold tabular-nums", color)}>
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Promote modal — confirm + optional name/status/dates.
// -----------------------------------------------------------------------------

function PromoteModal({
  plan,
  onClose,
  onDone,
}: {
  plan: PlanSummary;
  onClose: () => void;
  onDone: (campaignID: number) => void;
}) {
  const [form, setForm] = useState({
    campaign_name: plan.name,
    campaign_status: "planned",
    start_date: "",
    end_date: "",
  });
  const promoteMut = useMutation({
    mutationFn: () => endpoints.promotePlan(plan.id, form),
    onSuccess: (r) => onDone(r.campaign_id),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-background shadow-lg">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold">转为投放</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            会把 <strong>{plan.selected_count}</strong> 个入选频道一次性建成新投放,
            方案状态置为 promoted (不可再编辑)。
          </p>
        </div>
        <div className="space-y-3 px-5 py-4">
          <div>
            <label className="text-xs text-muted-foreground">投放名称</label>
            <input
              className="block w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={form.campaign_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, campaign_name: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">投放状态</label>
            <select
              className="block w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={form.campaign_status}
              onChange={(e) =>
                setForm((f) => ({ ...f, campaign_status: e.target.value }))
              }
            >
              <option value="planned">计划中</option>
              <option value="active">进行中</option>
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">开始日期</label>
              <input
                type="date"
                className="block w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.start_date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, start_date: e.target.value }))
                }
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">结束日期</label>
              <input
                type="date"
                className="block w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.end_date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, end_date: e.target.value }))
                }
              />
            </div>
          </div>
          {promoteMut.isError && (
            <p className="text-xs text-destructive">
              {(promoteMut.error as Error).message}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button
            size="sm"
            disabled={!form.campaign_name.trim() || promoteMut.isPending}
            onClick={() => promoteMut.mutate()}
          >
            {promoteMut.isPending ? "转换中…" : "转投放"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Format helpers — same as Plans.tsx.
// -----------------------------------------------------------------------------

function fmtUSD(v: number): string {
  if (v === Math.trunc(v)) return v.toLocaleString();
  return v.toFixed(2);
}

function fmtBigNumber(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}
